import styled from 'styled-components'
import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import Multiselect from 'multiselect-react-dropdown'

import { px2rem } from '@utils'
import Cities from '@services/cities'
import tokens from '@mch-group/design-tokens/build/js/design_tokens-module'
import tokensRenamed from '@constants/tokensRenamed'
import { Icon } from '@mch-group/uikit-components'

const StyledWrapper = styled.div`
  margin-bottom: ${px2rem(40)};
`

const StyledList = styled.li`
  &&& {
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: repeat(${props => props.rows}, 1fr);
    grid-template-columns: repeat(${props => props.columns}, 1fr);
    grid-gap: ${px2rem(2)};

    > .item {
      padding: 0;
      height: ${px2rem(24)};
      line-height: ${tokensRenamed.lineHeights[6].value};

      &.active {
        display: flex;
        align-items: center;
        font-weight: ${tokens.fontWeights.ivarDisplay3.value};
      }
    }
  }
`

const LocationFilterList = ({ selects: locationsQuery, selectsChanged, rows = 5,
  columns = 6, title, citiesLoaded }) => {
  const [selectedIds, setSelectedIds] = useState([])
  const [locations, setLocations] = useState({
    all: null,
    list: null,
    menu: null
  })

  /*
    locationsQuery is a query string with comma (,) divided locations.
    These locations have latitude, longitude and radius properties divided with a colon (:).
    example: "40.73061:-73.935242:25,51.509865:-0.118092:15"
  */
  const loadSelectedIds = useCallback((locationItems) => {
    const newSelectedIds = []

    if (locationsQuery) {
      locationsQuery.split(',').forEach((locationQuery) => {
        if (locationQuery) {
          const [qLatitude, qLongitude, qRadius] = locationQuery.split(':')
          const foundIndex = locationItems.findIndex(location =>
            location.latitude.toString() === qLatitude &&
            location.longitude.toString() === qLongitude &&
            location.radius.toString() === qRadius)

          if (foundIndex > -1) {
            newSelectedIds.push(locationItems[foundIndex].id)
          }
        }
      })
    }

    setSelectedIds(newSelectedIds)
  }, [locationsQuery])

  const toggleSelectedIds = (ids) => {
    const newSelectedIds = [...selectedIds]

    ids.forEach((locationId) => {
      const foundIndex = newSelectedIds.findIndex(id => id === locationId)

      if (foundIndex === -1) {
        newSelectedIds.push(locationId)
      } else {
        delete newSelectedIds[foundIndex]
      }
    })

    // Turn the selected city IDs into string form and send it to parent component for updating the query parameters
    if (locations.all) {
      const selectedLocs = locations.all.filter(l => newSelectedIds.includes(l.id))
      const newSelects = selectedLocs.map(l => `${l.latitude}:${l.longitude}:${l.radius}`)
      const newSelectsString = newSelects.join(',')
      const locationsQueryArray = locationsQuery ? locationsQuery.split(',') : []

      if (newSelects?.length !== locationsQueryArray?.length) {
        selectsChanged(newSelectsString)
      }
    }

    setSelectedIds(newSelectedIds)
  }

  const updateLocations = useCallback((items = locations.all) => {
    const listLength = (rows * columns) - 2
    setLocations({
      all: items,
      list: items.slice(0, listLength),
      menu: items.slice(listLength, items?.length).sort((a, b) => a.name > b.name || (a.name < b.name ? -1 : 0))
    })
  }, [locations.all, rows, columns])

  useEffect(() => {
    let unMounted = false
    if (!locations.all) {
      Cities.getList({ limit: 200, order: 'relevance', direction: 'desc' }).then(({ data }) => {
        if (data && data.items && !unMounted) {
          citiesLoaded(data.items)
          updateLocations(data.items)
          loadSelectedIds(data.items)
        }
      })
    } else {
      updateLocations()
    }
    return () => {
      unMounted = true
    }
  }, [locations.all, rows, columns, loadSelectedIds, updateLocations, citiesLoaded])

  // Effect that runs on change of default selected locations change. These selected locations are mostly being drilled
  // down from parent component, and they are mostly synchronized with query parameters.
  useEffect(() => {
    let unMounted = false
    if (locations.all && !unMounted) {
      loadSelectedIds(locations.all)
    }
    return () => {
      unMounted = true
    }
  }, [locationsQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  return locations.all ? (
    <StyledWrapper>
      <h2 className='h5 mb-4'>{title || 'Gallery Location'}</h2>
      <StyledList rows={rows} columns={columns}>
        {locations.list.map(location => (
          <span
            className='item'
            key={location.id}
            active={selectedIds.includes(location.id)}
            onClick={() => toggleSelectedIds([location.id])}
            onKeyUp={() => toggleSelectedIds([location.id])}
            tabIndex={0}
            role='button'
          >
            {location.name} {selectedIds.includes(location.id) && <Icon name='checkmark' />}
          </span>
        ))}
        <span className='item'>
          <Multiselect
            placeholder='Search other cities'
            avoidHighlightFirstOption
            showCheckbox
            options={locations.menu}
            selectedValues={locations.menu.filter((locationItem) => selectedIds.includes(locationItem?.id))}
            displayValue='name' // Property name to display in the dropdown options
            onSelect={(allValues, selectedValue) => {
              toggleSelectedIds([selectedValue.id])
            }}
            customCloseIcon={<Icon name='close' color='#ccc' size={18} className='ms-3' />}
            style={{
              chips: {
                background: '#ececec',
                color: 'black',
                borderRadius: '5px',
                padding: '5px'
              },
              option: {
                color: 'black'
              },
              searchBox: {
                border: 0,
                padding: 0
              },
              inputField: { // To change input field position or margin
                accentColor: '#FF69B4'
              }
            }}
          />
        </span>
      </StyledList>
    </StyledWrapper>
  ) : null
}

LocationFilterList.propTypes = {
  selects: PropTypes.string,
  selectsChanged: PropTypes.func,
  title: PropTypes.string,
  rows: PropTypes.number,
  columns: PropTypes.number,
  citiesLoaded: PropTypes.func
}

export default LocationFilterList
